import React from "react"
import { useQuery } from "@tanstack/react-query"
import api from "../../services/api"
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs"
import { Skeleton } from "../ui/skeleton"
import { Badge } from "../ui/badge"
import { Dumbbell, ArrowRight } from "lucide-react"

function ExerciseStats({ adminOnly }) {
  const { data, isLoading } = useQuery({
    queryKey: ["exercise-stats", adminOnly],
    queryFn: () => api.getExerciseStats(adminOnly),
    staleTime: 60000,
  })

  const exerciseData = {
    mostUsed: data?.mostUsed || [],
    mostReplaced: data?.mostReplaced || [],
    replacementPatterns: data?.replacementPatterns || [],
  }

  const formatReasonText = (reason) => {
    const reasonMap = {
      equipment_unavailable: "Equipment in use",
      injury_concern: "Injury concern",
      dislike: "Dislike exercise",
      too_tired: "Too tired",
      no_time: "No time",
      other: "Other",
    }
    return reasonMap[reason] || reason
  }

  const renderMostUsed = () => (
    <ol className="space-y-2">
      {exerciseData.mostUsed.map((exercise, index) => (
        <li
          key={index}
          className="flex items-center gap-3 p-2 rounded hover:bg-muted/50"
        >
          <span className="text-muted-foreground font-medium w-6 tabular-nums">
            {index + 1}.
          </span>
          <span className="flex-1 font-medium">{exercise.name}</span>
          <Badge variant="secondary">{exercise.count} times</Badge>
        </li>
      ))}
      {exerciseData.mostUsed.length === 0 && (
        <li className="text-muted-foreground text-center py-4">
          No exercise data available
        </li>
      )}
    </ol>
  )

  const renderMostReplaced = () => (
    <ol className="space-y-2">
      {exerciseData.mostReplaced.map((exercise, index) => (
        <li
          key={index}
          className="flex items-center gap-3 p-2 rounded hover:bg-muted/50"
        >
          <span className="text-muted-foreground font-medium w-6 tabular-nums">
            {index + 1}.
          </span>
          <span className="flex-1 font-medium">{exercise.name}</span>
          <Badge variant="destructive">{exercise.count} replaced</Badge>
        </li>
      ))}
      {exerciseData.mostReplaced.length === 0 && (
        <li className="text-muted-foreground text-center py-4">
          No replacement data available
        </li>
      )}
    </ol>
  )

  const renderReplacementPatterns = () => (
    <div className="space-y-3">
      {exerciseData.replacementPatterns.map((pattern, index) => (
        <div key={index} className="p-3 border rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <span className="font-medium text-destructive">
              {pattern.replacedExercise}
            </span>
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium text-primary">
              {pattern.replacementExercise}
            </span>
          </div>
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <Badge variant="outline">{pattern.count} times</Badge>
            {pattern.reason && (
              <span>
                Reason: {formatReasonText(pattern.reason)}
                {pattern.reason === "other" &&
                  pattern.otherReasons &&
                  pattern.otherReasons.length > 0 && (
                    <span> ({pattern.otherReasons.join(", ")})</span>
                  )}
              </span>
            )}
          </div>
        </div>
      ))}
      {exerciseData.replacementPatterns.length === 0 && (
        <div className="text-muted-foreground text-center py-4">
          No replacement patterns available
        </div>
      )}
    </div>
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Dumbbell className="h-5 w-5" />
          Exercise Analytics
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : (
          <Tabs defaultValue="mostUsed">
            <TabsList className="mb-4">
              <TabsTrigger value="mostUsed">Most Used</TabsTrigger>
              <TabsTrigger value="mostReplaced">Most Replaced</TabsTrigger>
              <TabsTrigger value="patterns">Replacement Patterns</TabsTrigger>
            </TabsList>
            <TabsContent value="mostUsed">{renderMostUsed()}</TabsContent>
            <TabsContent value="mostReplaced">{renderMostReplaced()}</TabsContent>
            <TabsContent value="patterns">{renderReplacementPatterns()}</TabsContent>
          </Tabs>
        )}
      </CardContent>
    </Card>
  )
}

export default ExerciseStats
